// O editor trabalha sobre uma cópia do catálogo. Campos que não têm controle na
// prévia continuam no rascunho e no payload de confirmação.
type CampoPreservado = { [key: string]: any };
export type ItemComplemento = CampoPreservado & { name: string; price: any; description?: string };
export type GrupoComplemento = CampoPreservado & {
  name: string; obrigatoriedade: 'obrigatorio' | 'opcional';
  sizeMin: number; sizeMax: number; itens: ItemComplemento[];
};
export type Tamanho = CampoPreservado & { name: string; sabores: number; _previewKey?: string };
export type Borda = CampoPreservado & { name: string; price: any };
export type Massa = CampoPreservado & { name: string; price: any };
export type ValorPizza = CampoPreservado & { name: string; valor: any; sabores: number };
export type Categoria = CampoPreservado & {
  id: string; nome: string; tipo: 'simples' | 'pizzas' | 'complementos';
  cobrarMaiorValor: boolean; tamanhos: Tamanho[]; bordas: Borda[]; massas: Massa[];
};
export type Produto = CampoPreservado & {
  id: string; categoriaId: string; nome: string; descricao: string;
  valor: any; valores: ValorPizza[]; grupoComplementos: GrupoComplemento[];
};
export type Preview = { categorias: Categoria[]; produtos: Produto[] };

export const novoIdPreview = (): string =>
  window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
const clone = (value: any): any => JSON.parse(JSON.stringify(value));
const lista = (value: any): any[] => Array.isArray(value) ? value : [];
const lowerTrim = (value: any): string => String(value ?? '').toLowerCase().trim();

export function normalizarPreviewCatalogo(raw: any): Preview {
  const canonico = Array.isArray(raw?.categoriaProdutos) && Array.isArray(raw?.catalogoProdutos);
  const cats: Categoria[] = lista(canonico ? raw.categoriaProdutos : raw?.categorias).map(input => {
    const c = clone(input);
    return {
      ...c, id: c.id || novoIdPreview(), nome: String(c.nome ?? ''),
      tipo: c.tipo === 'pizzas' ? 'pizzas' : c.tipo === 'complementos' ? 'complementos' : 'simples',
      cobrarMaiorValor: c.cobrarMaiorValor !== false,
      tamanhos: lista(c.tamanhos).map(t => ({
        ...t, name: String(t.name ?? ''), sabores: Number(t.sabores) > 0 ? Number(t.sabores) : 1,
        _previewKey: novoIdPreview(),
      })),
      bordas: lista(c.bordas).map(b => ({ ...b, name: String(b.name ?? ''), price: b.price ?? '0,00' })),
      massas: lista(c.massas).map(m => ({ ...m, name: String(m.name ?? ''), price: m.price ?? '0,00' })),
    };
  });
  const produtos: Produto[] = lista(canonico ? raw.catalogoProdutos : raw?.produtos).map(input => {
    const p = clone(input);
    let cat = p.categoriaId ? cats.find(c => c.id === p.categoriaId) : undefined;
    if (canonico && p.categoriaId && !cat) throw new Error(`Categoria do produto "${p.nome || 'sem nome'}" não encontrada na prévia.`);
    if (!cat) {
      const porNome = cats.filter(c => lowerTrim(c.nome) === lowerTrim(p.categoria));
      const porTipo = porNome.filter(c => c.tipo === p.tipo);
      const matches = porTipo.length ? porTipo : porNome;
      if (matches.length > 1) throw new Error('Há categorias com o mesmo nome. Confira o vínculo dos produtos antes de importar.');
      [cat] = matches;
    }
    if (!cat) {
      if (canonico) throw new Error(`Categoria do produto "${p.nome || 'sem nome'}" não encontrada na prévia.`);
      cat = { id: novoIdPreview(), nome: String(p.categoria || 'Sem categoria'), tipo: 'simples',
        cobrarMaiorValor: true, tamanhos: [], bordas: [], massas: [] };
      cats.push(cat);
    }
    return {
      ...p, id: p.id || novoIdPreview(), categoriaId: cat.id,
      nome: String(p.nome ?? ''), descricao: String(p.descricao ?? ''), valor: p.valor ?? '',
      // A leitura por foto não fornece as opções padrão do cadastro.
      ...(!canonico ? { internet: p.internet ?? true, unidadeMedida: p.unidadeMedida ?? 'unid' } : {}),
      valores: lista(p.valores).map(v => ({
        ...v, name: String(v.name ?? ''), valor: v.valor ?? '0,00',
        sabores: Number(v.sabores) > 0 ? Number(v.sabores) : 1,
      })),
      grupoComplementos: lista(p.grupoComplementos).map(g => ({
        ...g, id: g.id || novoIdPreview(), name: String(g.name ?? ''),
        obrigatoriedade: g.obrigatoriedade === 'obrigatorio' ? 'obrigatorio' : 'opcional',
        ...(!canonico ? {
          sizeMin: Number(g.sizeMin) >= 0 ? Number(g.sizeMin) : 0,
          sizeMax: g.sizeMax == null ? 1 : Math.max(0, Number(g.sizeMax) || 0),
        } : {}),
        itens: lista(g.itens).map(it => ({
          ...it, optionId: it.optionId || novoIdPreview(), name: String(it.name ?? ''),
          price: it.price ?? '0,00', description: String(it.description ?? ''),
        })),
      })),
    };
  });
  return { categorias: cats, produtos: sincronizarPizzas(cats, produtos) };
}

// A chave interna do tamanho acompanha renomeações e reordenações. Nunca é
// enviada à API. Um tamanho novo não herda por engano o preço de outro tamanho.
export function sincronizarPizzas(cats: Categoria[], prods: Produto[], anteriores?: Categoria[]): Produto[] {
  return prods.map(p => {
    const cat = cats.find(c => c.id === p.categoriaId);
    if (!cat) return p;
    if (cat.tipo === 'pizzas') {
      const catAntiga = anteriores?.find(c => c.id === p.categoriaId);
      const valores = cat.tamanhos.map((t, sizeIndex) => {
        const oldIndex = catAntiga?.tamanhos.findIndex(old => old._previewKey === t._previewKey) ?? -1;
        const old = oldIndex >= 0 ? catAntiga!.tamanhos[oldIndex] : undefined;
        const name = lowerTrim(old?.name ?? t.name);
        const matches = p.valores.filter(v => lowerTrim(v.name) === name);
        const occurrence = cat.tamanhos.slice(0, sizeIndex).filter(size => lowerTrim(size.name) === name).length;
        const existing = old ? p.valores[oldIndex] || matches[0] : matches[occurrence];
        return { ...(existing || {}), name: t.name, valor: existing?.valor ?? (p.valor === '' ? '0,00' : p.valor ?? '0,00'), sabores: t.sabores };
      });
      return { ...p, valores, valor: '' };
    }
    if (p.valores.length && !p.valor) return { ...p, valor: p.valores[0].valor ?? '0,00', valores: [] };
    return p.valores.length ? { ...p, valores: [] } : p;
  });
}

export function removerReferenciasPreview(preview: Preview, produtosRemovidos: string[], categoriasRemovidas: string[] = []): Preview {
  const removedProducts = new Set(produtosRemovidos);
  const removedCategories = new Set(categoriasRemovidas);
  return {
    categorias: preview.categorias.filter(c => !removedCategories.has(c.id)),
    produtos: preview.produtos.filter(p => !removedProducts.has(p.id)).map(p => {
      const next = { ...p };
      if (p.ofertasCarrinho) {
        const categoryIds = lista(p.ofertasCarrinho.categoryIds).filter(id => !removedCategories.has(id));
        const productIds = lista(p.ofertasCarrinho.productIds).filter(id => !removedProducts.has(id));
        const possuiAlvos = (p.ofertasCarrinho.type === 'PRODUCT' ? productIds : categoryIds).length > 0;
        next.ofertasCarrinho = { ...p.ofertasCarrinho, categoryIds, productIds,
          enabled: p.ofertasCarrinho.enabled && possuiAlvos };
      }
      if (Array.isArray(p.includedCategoryIds)) next.includedCategoryIds = p.includedCategoryIds.filter((id: string) => !removedCategories.has(id));
      next.grupoComplementos = p.grupoComplementos.map(g => ({ ...g, itens: g.itens.map(it => {
        if (!removedCategories.has(it.categoryId)) return it;
        const item = { ...it };
        delete item.categoryId;
        return item;
      }) }));
      return next;
    }),
  };
}

export function catalogoParaConfirmacao(cats: Categoria[], prods: Produto[]) {
  cats.filter(c => c.tipo === 'pizzas').forEach(c => {
    const nomes = c.tamanhos.map(t => lowerTrim(t.name));
    if (nomes.some(nome => !nome) || new Set(nomes).size !== nomes.length) {
      throw new Error(`Informe nomes diferentes para os tamanhos da categoria "${c.nome}" antes de confirmar.`);
    }
  });
  const categoriasPorId = new Map(cats.map(c => [c.id, c]));
  const produtosPorId = new Set(prods.map(p => p.id));
  prods.forEach(p => p.grupoComplementos.forEach(g => g.itens.forEach(it => {
    if (it.id && !produtosPorId.has(it.id)) {
      throw new Error(`O complemento "${it.name}" referencia um produto removido da prévia. Remova este complemento antes de confirmar.`);
    }
  })));
  const categoriaProdutos = cats.map(c => ({
    ...c, tamanhos: c.tamanhos.map(t => {
      const tamanho = { ...t };
      delete tamanho._previewKey;
      return tamanho;
    }),
  }));
  const catalogoProdutos = prods.map(p => {
    const cat = categoriasPorId.get(p.categoriaId);
    if (!cat) throw new Error(`Selecione a categoria do produto "${p.nome}" antes de confirmar.`);
    return { ...p, categoria: cat.nome, categoriaId: cat.id, tipo: cat.tipo };
  });
  return { categoriaProdutos, catalogoProdutos };
}
